//src/components/WeeklyCategoryManager.tsx
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Button } from "./Button"; // Use your actual Button path

export function WeeklyCategoryManager() {
  const [categories, setCategories] = useState<any[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchCategories() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data, error } = await supabase
        .from("user_categories")
        .select("id, category_name, description, priority")
        .eq("user_id", session.user.id)
        .order("priority", { ascending: true });

      if (error) {
        console.error("Failed to fetch categories:", error);
        setError("Could not load categories");
        return;
      }

      setCategories(data || []);
    }

    fetchCategories();
  }, []);

  const handleAddCategory = async () => {
    setIsLoading(true);
    setError("");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) throw new Error("User not logged in");

      const { data, error } = await supabase.from("user_categories").insert([
        {
          user_id: session.user.id,
          category_name: newCategory.trim(),
          description: newDescription.trim(),
          priority: categories.length + 1,
        },
      ]).select();

      if (error) throw error;

      setCategories((prev) => [
        ...prev,
        {
          id: data?.[0]?.id,
          category_name: newCategory.trim(),
          description: newDescription.trim(),
          priority: prev.length + 1,
        },
      ]);
      setNewCategory("");
      setNewDescription("");
    } catch (err: any) {
      console.error("Add category error:", err);
      setError(err.message || "Failed to add category");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("user_categories").delete().eq("id", id);
    if (error) {
      console.error("Delete failed:", error);
      return;
    }
    setCategories(categories.filter((cat) => cat.id !== id));
  };

  const moveCategory = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= categories.length) return;

    const updated = [...categories];
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];

    for (let i = 0; i < updated.length; i++) {
      updated[i].priority = i + 1;
      await supabase.from("user_categories").update({ priority: i + 1 }).eq("id", updated[i].id);
    }

    setCategories(updated);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {categories.map((cat, index) => (
          <div key={cat.id} className="p-3 border rounded bg-gray-50 space-y-1">
            <div className="flex justify-between items-start">
              <div>
                <strong>{cat.category_name}</strong>
                <div className="text-sm text-gray-600">{cat.description}</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => moveCategory(index, -1)}>↑</Button>
                <Button onClick={() => moveCategory(index, 1)}>↓</Button>
                <Button onClick={() => handleDelete(cat.id)} className="text-red-600">Delete</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <input
          type="text"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="New category"
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
        <input
          type="text"
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          placeholder="Category description"
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
        <Button onClick={handleAddCategory} disabled={isLoading || !newCategory.trim()}>
          {isLoading ? "Adding..." : "Add Category"}
        </Button>
        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>
    </div>
  );
}
